import React, { useEffect } from "react";
import styled from "styled-components";
import { useTranslation, Trans } from "react-i18next";
import { HashLink } from "react-router-hash-link";
import {
  ProjectDiagramIcon,
  RocketIcon,
  LockIcon,
  UsersIcon,
  CheckCircleIcon,
  ClockIcon,
  ToolsIcon,
  CodeIcon,
  DesktopIcon,
  LightbulbIcon,
} from "./IconComponents";
import SEO from "./SEO";

const PageContainer = styled.div`
  min-height: 100vh;
  padding: 2rem 0;
  background-color: var(--light-background);
`;

const HeroSection = styled.section`
  background: var(--primary-color);
  color: white;
  padding: 6rem 0 4rem;
  text-align: center;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const HeroTitle = styled.h1`
  font-size: 3.5rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.3rem;
  opacity: 0.9;
  max-width: 700px;
  margin: 0 auto 2rem;
  line-height: 1.6;
`;

const ContentSection = styled.section`
  padding: 4rem 0;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  text-align: center;
  margin-top: 4rem;
  margin-bottom: 2rem;
  color: var(--text-color);

  span {
    color: var(--primary-color);
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin: 3rem 0;
`;

const Card = styled.div`
  background: var(--card-background);
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  }
`;

const SectionTitleQuestion = styled.h2`
  font-size: 2.5rem;
  text-align: center;
  margin-top: 0rem;
  margin-bottom: 2rem;
  color: var(--text-color);

  span {
    color: var(--primary-color);
  }
`;

const IconWrapper = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: var(--primary-color);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  color: white;
  font-size: 1.5rem;
`;

const CardTitle = styled.h3`
  font-size: 1.4rem;
  margin-bottom: 1rem;
  color: var(--text-color);
`;

const CardDescription = styled.p`
  color: var(--text-color);
  line-height: 1.6;
`;

const ProcessSection = styled.section`
  background: var(--card-background);
  padding: 4rem 0;
`;

const ProcessGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin: 3rem 0;
`;

const ProcessCard = styled.div`
  text-align: center;
  padding: 2rem;
`;

const ProcessNumber = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: var(--primary-color);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: bold;
  margin: 0 auto 1.5rem;
`;

const ProcessTitle = styled.h4`
  font-size: 1.2rem;
  margin-bottom: 1rem;
  color: var(--text-color);
`;

const ProcessDescription = styled.p`
  color: var(--text-color);
  line-height: 1.6;
  font-size: 0.95rem;
`;

const BenefitsSection = styled.section`
  padding: 4rem 0;
  background-color: var(--light-background);
`;

const BenefitsList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
  margin: 3rem 0;
`;

const BenefitItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.5rem;
  background: var(--card-background);
  border-radius: 8px;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.05);
`;

const BenefitIcon = styled.div`
  color: var(--primary-color);
  font-size: 1.5rem;
  margin-top: 0.2rem;
  flex-shrink: 0;
`;

const BenefitText = styled.div`
  h5 {
    font-size: 1.1rem;
    margin-bottom: 0.5rem;
    color: var(--text-color);
  }

  p {
    color: var(--text-color);
    line-height: 1.5;
    font-size: 0.95rem;
  }
`;

const TechnologySection = styled.section`
  background: var(--card-background);
  padding: 4rem 0;
`;

const TechnologyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 2rem;
  margin: 3rem 0;
`;

const TechCard = styled.div`
  text-align: center;
  padding: 1.5rem;
  border-radius: 8px;
  background: var(--light-background);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  }
`;

const TechIcon = styled.div`
  color: var(--primary-color);
  font-size: 2rem;
  margin-bottom: 1rem;
`;

const TechName = styled.h4`
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
  color: var(--text-color);
`;

const TechDescription = styled.p`
  color: var(--text-color);
  font-size: 0.9rem;
  line-height: 1.4;
`;

const CaseStudySection = styled.section`
  padding: 4rem 0;
  background-color: var(--light-background);
`;

const CaseStudyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
  margin: 3rem 0;
`;

const CaseStudyCard = styled.div`
  background: var(--card-background);
  border-radius: 12px;
  padding: 2.5rem;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
  position: relative;
  overflow: hidden;

  &:before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 4px;
    background: linear-gradient(
      90deg,
      var(--primary-color),
      var(--secondary-color)
    );
  }
`;

const CaseStudyTitle = styled.h4`
  font-size: 1.3rem;
  margin-bottom: 1rem;
  color: var(--text-color);
`;

const CaseStudyText = styled.p`
  color: var(--text-color);
  line-height: 1.6;
  margin-bottom: 1rem;
`;

const CaseStudyResult = styled.div`
  background: var(--light-background);
  padding: 1rem;
  border-radius: 6px;
  border-left: 4px solid var(--primary-color);

  h5 {
    color: var(--primary-color);
    margin-bottom: 0.5rem;
    font-size: 1rem;
  }

  p {
    color: var(--text-color);
    font-size: 0.9rem;
    margin: 0;
  }
`;

const ServiceTypesSection = styled.section`
  background: var(--card-background);
  padding: 4rem 0;
`;

const ServiceTypeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
  margin: 3rem 0;
`;

const ServiceTypeCard = styled.div`
  background: var(--light-background);
  border-radius: 12px;
  padding: 2rem;
  text-align: center;
  position: relative;
  border: 2px solid transparent;
  transition: all 0.3s ease;

  &:hover {
    border-color: var(--primary-color);
    transform: translateY(-5px);
  }
`;

const ServiceTypeTitle = styled.h4`
  font-size: 1.4rem;
  margin-bottom: 1rem;
  color: var(--text-color);
`;

const ServiceTypeDescription = styled.p`
  color: var(--text-color);
  margin-bottom: 1.5rem;
  font-size: 0.95rem;
  line-height: 1.5;
`;

const ServiceTypeFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;

  li {
    color: var(--text-color);
    padding: 0.5rem 0;
    font-size: 0.9rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;

    &:before {
      content: "✓";
      color: var(--primary-color);
      font-weight: bold;
    }
  }
`;

const FAQSection = styled.section`
  padding: 4rem 0;
  background-color: var(--light-background);
`;

const FAQContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const FAQItem = styled.div`
  background: var(--card-background);
  margin-bottom: 1rem;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
`;

const FAQQuestion = styled.h4`
  padding: 1.5rem;
  margin: 0;
  background: var(--primary-color);
  color: white;
  font-size: 1.1rem;
  cursor: pointer;
  transition: background-color 0.3s ease;

  &:hover {
    background: var(--secondary-color);
  }
`;

const FAQAnswer = styled.p`
  padding: 1.5rem;
  margin: 0;
  color: var(--text-color);
  line-height: 1.6;
`;

const CTASection = styled.section`
  background: var(--primary-color);
  color: white;
  padding: 4rem 0;
  text-align: center;
`;

const CTATitle = styled.h3`
  font-size: 2rem;
  margin-bottom: 1rem;
`;

const CTADescription = styled.p`
  font-size: 1.1rem;
  margin-bottom: 2rem;
  opacity: 0.9;
`;

const CTAContainer = styled.div`
  text-align: center;
  margin-top: 4rem;
  margin-bottom: 2rem;
  padding: 3rem;
  background-color: var(--card-background);
  border-radius: 8px;
`;

const CTAButton = styled(HashLink)`
  display: block;
  width: 50%;
  margin: 2rem auto 0;
  padding: 1rem 1.5rem;
  border: none;
  border-radius: 4px;
  background-color: var(--primary-color);
  color: var(--light-text-color);
  font-size: 1.05rem;
  font-weight: 600;
  text-align: center;
  text-decoration: none;
  cursor: pointer;
  transition: var(--transition);

  &:hover {
    background-color: var(--secondary-color);
    transform: translateY(-2px);
  }

  &:focus-visible {
    outline: 3px solid var(--primary-color);
    outline-offset: 4px;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.2rem;
  text-align: center;
  max-width: 800px;
  margin: 0 auto 3rem;
  color: var(--text-color);
`;

const Projektmanagement: React.FC = () => {
  const { t, i18n } = useTranslation();

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const advantages = [
    {
      icon: <RocketIcon />,
      title: "Klarer Projektüberblick",
      description:
        "Behalten Sie jederzeit den vollständigen Überblick über Zeitpläne, Budgets, Ressourcen und Meilensteine Ihrer Projekte.",
    },
    {
      icon: <ClockIcon />,
      title: "Termintreue",
      description:
        "Durch strukturierte Planung und kontinuierliches Controlling erreichen wir Ihre Projektziele termingerecht.",
    },
    {
      icon: <UsersIcon />,
      title: "Effiziente Zusammenarbeit",
      description:
        "Wir koordinieren alle Beteiligten und sorgen für reibungslose Kommunikation zwischen allen Stakeholdern.",
    },
    {
      icon: <LockIcon />,
      title: "Risikominimierung",
      description:
        "Frühzeitige Identifikation und aktives Management von Projektrisiken schützt Ihr Budget und Ihren Zeitplan.",
    },
  ];

  const process = [
    {
      title: "Initiierung & Zieldefinition",
      description:
        "Gemeinsam definieren wir klare Projektziele, Umfang, Budget und Erfolgskriterien.",
    },
    {
      title: "Planung",
      description:
        "Detaillierte Projekt-, Ressourcen- und Zeitplanung inklusive Risikoanalyse und Kommunikationsstrategie.",
    },
    {
      title: "Durchführung & Steuerung",
      description:
        "Aktive Steuerung des Projektteams, laufendes Controlling und transparentes Reporting an alle Stakeholder.",
    },
    {
      title: "Abschluss & Lessons Learned",
      description:
        "Strukturierter Projektabschluss mit Ergebnisdokumentation und Ableitung von Optimierungspotenzialen.",
    },
  ];

  const benefits = [
    {
      title: "Budgetkontrolle",
      description:
        "Transparente Kostenverfolgung und proaktives Gegensteuern bei Abweichungen sichern Ihr Budget.",
    },
    {
      title: "Höhere Erfolgsquote",
      description:
        "Strukturiertes Projektmanagement erhöht signifikant die Wahrscheinlichkeit erfolgreicher Projektabschlüsse.",
    },
    {
      title: "Skalierbare Prozesse",
      description:
        "Bewährte Methoden lassen sich flexibel auf Projekte jeder Größe und Komplexität anwenden.",
    },
    {
      title: "Transparente Kommunikation",
      description:
        "Regelmäßiges Reporting sorgt für Klarheit bei allen Projektbeteiligten und Entscheidungsträgern.",
    },
    {
      title: "Agilität & Flexibilität",
      description:
        "Wir kombinieren klassische und agile Methoden, um optimal auf Veränderungen reagieren zu können.",
    },
    {
      title: "Nachhaltige Ergebnisse",
      description:
        "Sorgfältige Dokumentation und Wissenstransfer sichern den langfristigen Projekterfolg.",
    },
  ];

  const technologies = [
    {
      icon: <ProjectDiagramIcon />,
      name: "Klassisches PM",
      description: "Wasserfall, PMI/PMBOK, Prince2",
    },
    {
      icon: <ToolsIcon />,
      name: "Agiles PM",
      description: "Scrum, Kanban, SAFe",
    },
    {
      icon: <CodeIcon />,
      name: "PM-Tools",
      description: "Jira, Microsoft Project, Asana, Trello",
    },
    {
      icon: <DesktopIcon />,
      name: "Reporting & Controlling",
      description: "Dashboards, KPI-Tracking, Budgetplanung",
    },
    {
      icon: <UsersIcon />,
      name: "Stakeholder-Management",
      description: "Kommunikationspläne, Change Management",
    },
    {
      icon: <LightbulbIcon />,
      name: "Risikomanagement",
      description: "Risikoanalyse, Präventions- und Notfallpläne",
    },
  ];

  const caseStudies = [
    {
      title: "Einführung eines neuen ERP-Systems",
      description:
        "Steuerung eines unternehmensweiten ERP-Rollouts mit über 15 beteiligten Fachabteilungen und straffem Zeitplan.",
      result: {
        title: "Ergebnisse:",
        description:
          "Projektabschluss termingerecht, 10% unter Budget, reibungsloser Go-Live ohne Betriebsunterbrechung",
      },
    },
    {
      title: "Digitalisierungsprojekt im Mittelstand",
      description:
        "Koordination eines mehrjährigen Digitalisierungsprogramms mit mehreren Teilprojekten und externen Dienstleistern.",
      result: {
        title: "Ergebnisse:",
        description:
          "Vollständige Transparenz über alle Teilprojekte, 25% schnellere Entscheidungsfindung, erfolgreicher Abschluss aller Meilensteine",
      },
    },
    {
      title: "Agile Transformation eines IT-Teams",
      description:
        "Begleitung der Umstellung von klassischem auf agiles Projektmanagement inklusive Coaching des Teams.",
      result: {
        title: "Ergebnisse:",
        description:
          "40% kürzere Release-Zyklen, höhere Teamzufriedenheit, verbesserte Reaktionsfähigkeit auf Änderungen",
      },
    },
  ];

  const serviceTypes = [
    {
      title: "Projektmanagement-Beratung",
      description:
        "Wir unterstützen Sie bei der Auswahl und Einführung der passenden Projektmanagement-Methodik für Ihr Unternehmen.",
      features: [
        "Methodenauswahl (klassisch/agil/hybrid)",
        "Prozess- und Tool-Beratung",
        "Aufbau von PM-Standards",
        "Schulung von Projektleitern",
        "Reifegrad-Analyse",
      ],
    },
    {
      title: "Projektleitung & -steuerung",
      description:
        "Übernahme der operativen Projektleitung für Ihre IT- und Digitalisierungsprojekte von der Planung bis zum Abschluss.",
      features: [
        "Projektplanung und Budgetierung",
        "Teamkoordination und Steuerung",
        "Stakeholder-Kommunikation",
        "Risiko- und Qualitätsmanagement",
        "Reporting und Statusverfolgung",
      ],
    },
    {
      title: "Interim- & Programmmanagement",
      description:
        "Erfahrene Projektmanager auf Zeit für komplexe Programme mit mehreren parallelen Teilprojekten.",
      features: [
        "Multi-Projekt-Koordination",
        "Priorisierung und Ressourcenplanung",
        "Krisenmanagement",
        "Executive Reporting",
        "Nachhaltige Wissensübergabe",
      ],
    },
  ];

  const faqs = [
    {
      question: "Welche Projektmanagement-Methoden nutzen Sie?",
      answer:
        "Wir setzen je nach Projektanforderung auf klassische Methoden wie PMI/PMBOK oder Prince2, agile Ansätze wie Scrum und Kanban, oder hybride Modelle, die beide Welten kombinieren.",
    },
    {
      question: "Übernehmen Sie auch die komplette Projektleitung?",
      answer:
        "Ja, wir bieten sowohl beratende Unterstützung als auch die vollständige operative Übernahme der Projektleitung, inklusive Steuerung von Teams, Budgets und Zeitplänen.",
    },
    {
      question: "Wie stellen Sie die Termintreue eines Projekts sicher?",
      answer:
        "Durch realistische Planung, kontinuierliches Controlling, frühzeitiges Risikomanagement und transparente Kommunikation mit allen Stakeholdern behalten wir Zeitpläne im Blick und steuern proaktiv gegen.",
    },
    {
      question: "Für welche Projektgrößen eignet sich Ihr Angebot?",
      answer:
        "Unsere Methoden und Prozesse sind skalierbar und eignen sich sowohl für kleinere Einzelprojekte als auch für umfangreiche, mehrjährige Programme mit vielen Teilprojekten.",
    },
    {
      question: "Wie werden Projektrisiken gehandhabt?",
      answer:
        "Wir identifizieren Risiken frühzeitig durch strukturierte Risikoanalysen und erarbeiten gemeinsam mit Ihnen Präventions- und Notfallpläne, um den Projekterfolg abzusichern.",
    },
  ];

  return (
    <PageContainer>
      <SEO
        title="Professionelles Projektmanagement für Softwareprojekte"
        description="Strukturiertes Projektmanagement von Planung bis Abschluss: termingerecht, budgetgenau und transparent. Byte Forge steuert Ihre IT-Projekte zum Erfolg."
        path="/projektmanagement"
        keywords="Projektmanagement, IT-Projektmanagement, Projektsteuerung, agiles Projektmanagement"
      />
      <Container>
        <SectionTitle>
          <Trans>
            Professionelles Projektmanagement für <span>Ihren Erfolg</span>
          </Trans>
        </SectionTitle>
        <SectionSubtitle>
          Maßgeschneiderte Softwarelösungen, die perfekt auf Ihre
          Geschäftsanforderungen zugeschnitten sind. Wir entwickeln innovative,
          skalierbare und sichere Anwendungen, die Ihrem Unternehmen einen
          echten Wettbewerbsvorteil verschaffen.
        </SectionSubtitle>
      </Container>

      <ContentSection>
        <Container>
          <SectionTitleQuestion>
            Warum <span>Projektmanagement</span>?
          </SectionTitleQuestion>
          <Grid>
            {advantages.map((advantage, index) => (
              <Card key={index}>
                <IconWrapper>{advantage.icon}</IconWrapper>
                <CardTitle>{advantage.title}</CardTitle>
                <CardDescription>{advantage.description}</CardDescription>
              </Card>
            ))}
          </Grid>
        </Container>
      </ContentSection>

      <ProcessSection>
        <Container>
          <SectionTitle>
            Unser <span>Projektmanagement-Prozess</span>
          </SectionTitle>
          <ProcessGrid>
            {process.map((step, index) => (
              <ProcessCard key={index}>
                <ProcessNumber>{index + 1}</ProcessNumber>
                <ProcessTitle>{step.title}</ProcessTitle>
                <ProcessDescription>{step.description}</ProcessDescription>
              </ProcessCard>
            ))}
          </ProcessGrid>
        </Container>
      </ProcessSection>

      <BenefitsSection>
        <Container>
          <SectionTitle>
            Ihre <span>Vorteile</span>
          </SectionTitle>
          <BenefitsList>
            {benefits.map((benefit, index) => (
              <BenefitItem key={index}>
                <BenefitIcon>
                  <CheckCircleIcon />
                </BenefitIcon>
                <BenefitText>
                  <h5>{benefit.title}</h5>
                  <p>{benefit.description}</p>
                </BenefitText>
              </BenefitItem>
            ))}
          </BenefitsList>
        </Container>
      </BenefitsSection>

      <TechnologySection>
        <Container>
          <SectionTitle>
            Unsere <span>Methoden & Tools</span>
          </SectionTitle>
          <TechnologyGrid>
            {technologies.map((tech, index) => (
              <TechCard key={index}>
                <TechIcon>{tech.icon}</TechIcon>
                <TechName>{tech.name}</TechName>
                <TechDescription>{tech.description}</TechDescription>
              </TechCard>
            ))}
          </TechnologyGrid>
        </Container>
      </TechnologySection>

      <CaseStudySection>
        <Container>
          <SectionTitle>
            Erfolgreiche <span>Projekte</span>
          </SectionTitle>
          <CaseStudyGrid>
            {caseStudies.map((caseStudy, index) => (
              <CaseStudyCard key={index}>
                <CaseStudyTitle>{caseStudy.title}</CaseStudyTitle>
                <CaseStudyText>{caseStudy.description}</CaseStudyText>
                <CaseStudyResult>
                  <h5>{caseStudy.result.title}</h5>
                  <p>{caseStudy.result.description}</p>
                </CaseStudyResult>
              </CaseStudyCard>
            ))}
          </CaseStudyGrid>
        </Container>
      </CaseStudySection>

      <ServiceTypesSection>
        <Container>
          <SectionTitle>
            Unsere <span>PM-Services</span>
          </SectionTitle>
          <ServiceTypeGrid>
            {serviceTypes.map((service, index) => (
              <ServiceTypeCard key={index}>
                <ServiceTypeTitle>{service.title}</ServiceTypeTitle>
                <ServiceTypeDescription>
                  {service.description}
                </ServiceTypeDescription>
                <ServiceTypeFeatures>
                  {service.features.map((feature, featureIndex) => (
                    <li key={featureIndex}>{feature}</li>
                  ))}
                </ServiceTypeFeatures>
              </ServiceTypeCard>
            ))}
          </ServiceTypeGrid>
        </Container>
      </ServiceTypesSection>

      <FAQSection>
        <Container>
          <SectionTitle>
            Häufig gestellte <span>Fragen</span>
          </SectionTitle>
          <FAQContainer>
            {faqs.map((faq, index) => (
              <FAQItem key={index}>
                <FAQQuestion>{faq.question}</FAQQuestion>
                <FAQAnswer>{faq.answer}</FAQAnswer>
              </FAQItem>
            ))}
          </FAQContainer>
        </Container>
      </FAQSection>

      <Container>
        <CTAContainer>
          <SectionTitleQuestion>
            <Trans>
              Bereit für <span>erfolgreiches Projektmanagement?</span>
            </Trans>
          </SectionTitleQuestion>
          <SectionSubtitle>
            Lassen Sie uns gemeinsam Ihr nächstes Projekt strukturiert und
            erfolgreich zum Ziel führen.
            <CTAButton smooth to="/#contact">
              Kostenloses Erstgespräch
            </CTAButton>
          </SectionSubtitle>
        </CTAContainer>
      </Container>

    </PageContainer>
  );
};

export default Projektmanagement;
